import React, { createContext, ReactChild, useReducer } from "react";
import { Logger } from "../logging/logger";

export interface ExperimentImpression {
  experimentKey: string;
  variationKey: string;
}

export interface ProductPageAnalyticsState {
  trackingInitialized: boolean;

  // Optimizely experiment data
  experimentImpression?: ExperimentImpression;
}

export type AnalyticsContextType = ProductPageAnalyticsState & {
  decision?: string;
  analyticsPageName: (mpvId: string) => string;
  setTrackingInitialized: (trackingInitialized: boolean) => void;
};

export const AnalyticsContext = createContext<AnalyticsContextType>({
  trackingInitialized: false,
  experimentImpression: undefined,
  decision: undefined,
  analyticsPageName: () => "",
  setTrackingInitialized: () => {},
});

export type AnalyticsContextProviderProps = {
  experimentImpression?: ExperimentImpression;
  decision?: string;
  children?: ReactChild | ReactChild[] | undefined | null;
};

export const AnalyticsContextProvider = (props: AnalyticsContextProviderProps) => {
  const initialState: ProductPageAnalyticsState = {
    trackingInitialized: false,
  };
  const analyticsReducer = (state: ProductPageAnalyticsState, action: any) => {
    switch (action.type) {
      case "windowTrackingInitialized": {
        return {
          ...state,
          trackingInitialized: action.trackingInitialized,
        };
      }
      default: {
        Logger.instance.error("Unknown action in analytics context", {
          actionType: action.type,
        });
        return { ...state };
      }
    }
  };
  const [state, dispatch] = useReducer(analyticsReducer, initialState);
  const { trackingInitialized } = state;
  const setTrackingInitialized = (trackingInitialized: boolean) => {
    dispatch({
      type: "windowTrackingInitialized",
      trackingInitialized: trackingInitialized,
    });
  };

  const analyticsPageName = (mpvId: string) => `${mpvId}:Product Page`;

  const value = {
    trackingInitialized,
    experimentImpression: props.experimentImpression,
    decision: props.decision,
    analyticsPageName,
    setTrackingInitialized,
  };

  return <AnalyticsContext.Provider value={value}>{props.children}</AnalyticsContext.Provider>;
};
